/**
 * ChatMessageList - Renders chat messages with loading indicator
 * Extracted from App.tsx for modularization
 * 
 * Supports:
 * - Text messages (markdown)
 * - Image messages
 * - Web search results (collapsible inline)
 *   - In-house products have cart/wishlist action buttons
 *   - Web products have external links
 *   - Row 3: Sponsored/Amazon products (affiliate)
 */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import CollapsibleWebResults from './CollapsibleWebResults';
import type { ChatMessage, WebSearchResultItem } from '../../types/conversation';
import type { CartItem, WishlistItem, GridProductCard } from '../../types/product';

/** Max messages to render at once to keep the DOM lightweight */
const DISPLAY_LIMIT = 50;
/** How many more to show when user clicks "Show earlier" */
const DISPLAY_PAGE_SIZE = 30;

interface ChatMessageListProps {
  messages: ChatMessage[];
  isLoading: boolean;
  language?: string;
  onWebResultClick?: (result: WebSearchResultItem, index: number) => void;
  /** Callback to add a product to cart (for in-house products in search results) */
  onAddToCart?: (item: CartItem) => void;
  /** Callback to toggle wishlist (for in-house products in search results) */
  onWishlistToggle?: (item: WishlistItem, source?: 'web-search') => void;
  /** Function to check if a product is in the wishlist */
  isInWishlist?: (productId: string) => boolean;
  /** Callback when "View Details" is clicked on an in-house product */
  onViewDetails?: (product: WebSearchResultItem) => void;
  /** Row 3: Sponsored/Amazon products */
  sponsoredProducts?: GridProductCard[];
  /** Loading state for sponsored products */
  sponsoredLoading?: boolean;
  /** Callback when sponsored product is clicked (for tracking) */
  onSponsoredClick?: (product: GridProductCard) => void;
  /** Callback to ask AI about a specific product (bridges Search → Chat) */
  onAskAI?: (productName: string) => void;
  /** Callback to dismiss an error message by ID */
  onDismissError?: (messageId: string) => void;
}

const isHtmlPage = (text: string): boolean =>
  text.trimStart().startsWith('<!DOCTYPE') || text.trimStart().startsWith('<html');

const renderMessageText = (text: string) => {
  // Guard: never render raw HTML page content (from past API fallback errors)
  if (isHtmlPage(text)) {
    return <em className="chat-message-load-error">⚠️ This message failed to load.</em>;
  }
  return (
    <ReactMarkdown
      components={{
        a: ({ node, ...props }) => (
          <a {...props} target="_blank" rel="noopener noreferrer">
            {props.children}
          </a>
        ),
      }}
    >
      {text}
    </ReactMarkdown>
  );
};

export default function ChatMessageList({ 
  messages, 
  isLoading, 
  language = 'ko',
  onWebResultClick,
  onAddToCart,
  onWishlistToggle,
  isInWishlist,
  onViewDetails,
  sponsoredProducts = [],
  sponsoredLoading = false,
  onSponsoredClick,
  onAskAI,
  onDismissError,
}: ChatMessageListProps) {
  // Don't show typing indicator if last message is already a bot message (streaming in progress)
  const lastMessage = messages[messages.length - 1];
  const isStreaming = lastMessage?.sender === 'bot' && isLoading;
  const showTypingIndicator = isLoading && !isStreaming;

  // --- Display limit: only render the most recent N messages ---
  const [displayCount, setDisplayCount] = useState(DISPLAY_LIMIT);

  // Reset displayCount when the conversation changes (different message set)
  const prevFirstMsgId = useRef(messages[0]?.id);
  useEffect(() => {
    const firstId = messages[0]?.id;
    if (firstId !== prevFirstMsgId.current) {
      prevFirstMsgId.current = firstId;
      setDisplayCount(DISPLAY_LIMIT);
    }
  }, [messages]);

  const visibleMessages = useMemo(() => {
    if (messages.length <= displayCount) return messages;
    return messages.slice(messages.length - displayCount);
  }, [messages, displayCount]);
  const hasHiddenMessages = messages.length > displayCount;
  // Offset so that lastWebSearchIndex is computed against the full array
  const visibleOffset = messages.length - visibleMessages.length;
  
  // Find index of last web-search message — scan backward for O(1)-ish early exit
  const lastWebSearchIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].type === 'web-search') return i;
    }
    return -1;
  }, [messages]);

  const { t } = useTranslation('chat');
  const [disclaimerDismissed, setDisclaimerDismissed] = useState(
    () => { try { return localStorage.getItem('healthDisclaimerDismissed') === 'true'; } catch { return false; } }
  );

  // Auto-dismiss health disclaimer after 15s for returning users (session-only).
  // First-time users (no prior visits) see it until they manually dismiss.
  // Auto-dismiss only hides for the current session — does NOT persist to localStorage.
  useEffect(() => {
    if (disclaimerDismissed) return;
    // Only auto-dismiss for returning users who have seen it before
    try {
      const visitCount = parseInt(localStorage.getItem('heseda_visit_count') || '0', 10);
      localStorage.setItem('heseda_visit_count', String(visitCount + 1));
      if (visitCount < 1) return; // First visit — don't auto-dismiss
    } catch { return; }
    const timer = setTimeout(() => {
      setDisclaimerDismissed(true);
      // Intentionally NOT writing to localStorage here — auto-dismiss is session-only.
      // Permanent dismissal only happens when user clicks the X button.
    }, 15000);
    return () => clearTimeout(timer);
  }, [disclaimerDismissed]);
  
  return (
    <>
      {!disclaimerDismissed && (
        <div className="health-disclaimer-banner">
          <span className="health-disclaimer-icon">&#9432;</span>
          <span>{t('healthDisclaimer')}</span>
          <button
            className="health-disclaimer-close"
            onClick={() => {
              setDisclaimerDismissed(true);
              try { localStorage.setItem('healthDisclaimerDismissed', 'true'); } catch { /* storage unavailable */ }
            }}
            aria-label={t('dismissHealthDisclaimer')}
          >
            &times;
          </button>
        </div>
      )}
      {hasHiddenMessages && (
        <button
          className="show-earlier-btn"
          data-testid="show-earlier-btn"
          onClick={() => setDisplayCount(prev => prev + DISPLAY_PAGE_SIZE)}
        >
          {t('showEarlierMessages', {
            count: messages.length - displayCount,
            defaultValue: 'Show earlier messages ({{count}} hidden)',
          })}
        </button>
      )}
      {visibleMessages.map((message, visibleIndex) => {
        const index = visibleIndex + visibleOffset;
        // Web search results message - render as collapsible card
        if (message.type === 'web-search') {
          // Only show Row 3 (sponsored) on the last web-search message
          const isLastWebSearch = index === lastWebSearchIndex;
          
          return (
            <div
              key={message.id || `ws-${index}`}
              data-testid={`chat-message-${index}`}
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                width: '100%',
              }}
            >
              <div className="message bot web-search-message">
                <CollapsibleWebResults
                  results={message.webSearchResults ?? []}
                  sponsoredProducts={isLastWebSearch ? sponsoredProducts : []}
                  sponsoredLoading={isLastWebSearch ? sponsoredLoading : false}
                  language={language}
                  defaultExpanded={true}
                  onClickResult={onWebResultClick}
                  onAddToCart={onAddToCart}
                  onWishlistToggle={onWishlistToggle}
                  isInWishlist={isInWishlist}
                  onViewDetails={onViewDetails}
                  onSponsoredClick={onSponsoredClick}
                  onAskAI={onAskAI}
                  searchTriggerSource={message.searchTriggerSource}
                />
              </div>
            </div>
          );
        }

        // Regular message (text or image)

        return (
          <div
            key={message.id || `msg-${index}`}
            data-testid={`chat-message-${index}`}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: message.sender === 'user' ? 'flex-end' : 'flex-start',
              width: '100%',
            }}
          >
            <div className={`message ${message.sender}${message.isError ? ' message--error' : ''}`}>
              {message.sender === 'bot' && (
                <span className="message__ai-badge" aria-hidden="true">AI</span>
              )}
              {message.imageUrl ? (
                <img src={message.imageUrl} alt="Uploaded Preview" className="uploaded-image" />
              ) : (
                renderMessageText(message.text || '')
              )}
              {message.isError && onDismissError && (
                <button
                  className="message__dismiss-error"
                  onClick={() => onDismissError(message.id)}
                  aria-label="Dismiss error"
                >
                  &times;
                </button>
              )}
            </div>
            {/* Photo re-upload chip removed — camera button is always visible in the chat input bar */}
          </div>
        );
      })}
      {showTypingIndicator && (
        <div className="message bot loading">
          <div className="spinner">
            <div className="double-bounce1"></div>
            <div className="double-bounce2"></div>
          </div>
          <span>{lastMessage?.imageUrl ? `📸 ${t('analyzing')}` : t('typing')}</span>
        </div>
      )}
    </>
  );
}
